import React from 'react';
import { motion } from 'framer-motion';
import { Icons } from '../../constants';

// --- Tokens & Styles ---

export const sidebarTokens = {
    bg: "bg-ui-panel backdrop-blur-3xl",
    border: "border-ui-border",
    text: {
        primary: "text-text-main",
        secondary: "text-text-muted",
        muted: "text-text-muted/60",
        hover: "group-hover:text-text-main",
        active: "text-text-main"
    },
    hover: "hover:bg-ui-surface/50",
    active: "bg-ui-surface shadow-inner shadow-ui-ring/10",
    transition: "transition-all duration-200 ease-out"
};

// --- Animations ---

export const sidebarAnimations = {
    slideInRight: {
        initial: { x: '100%', opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: '100%', opacity: 0 },
        transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    slideInLeft: {
        initial: { x: '-100%', opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: '-100%', opacity: 0 },
        transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    fadeIn: {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.2 }
    }
};

// --- Components ---

interface SidebarContainerProps extends React.HTMLAttributes<HTMLElement> {
    side?: 'left' | 'right';
    width?: string;
    collapsed?: boolean;
}

export const SidebarContainer: React.FC<SidebarContainerProps> = ({
    children,
    side = 'left',
    width = 'w-64',
    collapsed = false,
    className = "",
    ...props
}) => {
    return (
        <aside
            className={`
                fixed top-0 bottom-0 z-[40] flex flex-col
                ${side === 'left' ? 'left-0 border-r' : 'right-0 border-l'}
                ${width} ${collapsed ? 'w-[64px]' : ''}
                ${sidebarTokens.bg} ${sidebarTokens.border}
                ${className}
            `}
            {...props}
        >
            {children}
        </aside>
    );
};

interface SidebarHeaderProps {
    title?: string;
    icon?: React.ReactNode;
    onClose?: () => void;
    rightContent?: React.ReactNode;
    className?: string;
    side?: 'left' | 'right';
}

export const SidebarHeader: React.FC<SidebarHeaderProps> = ({
    title,
    icon,
    onClose,
    rightContent,
    className = "",
    side = 'right'
}) => {
    return (
        <div className={`flex items-center justify-between px-4 py-3 border-b ${sidebarTokens.border} ${className}`}>
            <div className="flex items-center gap-2">
                {icon && <span className={sidebarTokens.text.secondary}>{icon}</span>}
                {title && (
                    <h2 className={`text-[11px] font-bold tracking-widest uppercase select-none ${sidebarTokens.text.muted}`}>
                        {title}
                    </h2>
                )}
            </div>
            <div className="flex items-center gap-1">
                {rightContent}
                {onClose && (
                    <button
                        onClick={onClose}
                        className={`p-1.5 rounded-md ${sidebarTokens.hover} ${sidebarTokens.text.secondary} hover:text-text-main transition-colors`}
                    >
                        {side === 'right' ? <Icons.ChevronRight className="w-3.5 h-3.5" /> : <Icons.ChevronLeft className="w-3.5 h-3.5" />}
                    </button>
                )}
            </div>
        </div>
    );
};

// Helper for close button which needs side context - updated above to dynamic check but keeping simple prop driven for now 
// Redefining simpler version:

export const SidebarCloseButton: React.FC<{ onClick: () => void, side?: 'left' | 'right' }> = ({ onClick, side = 'right' }) => (
    <button
        onClick={onClick}
        className={`p-1.5 rounded-md ${sidebarTokens.hover} ${sidebarTokens.text.secondary} hover:text-text-main transition-colors`}
    >
        {side === 'right' ? <Icons.ChevronRight className="w-3.5 h-3.5" /> : <Icons.ChevronLeft className="w-3.5 h-3.5" />}
    </button>
);


interface SidebarSectionProps {
    title?: string;
    children: React.ReactNode;
    className?: string;
}

export const SidebarSection: React.FC<SidebarSectionProps> = ({ title, children, className = "" }) => (
    <div className={`py-2.5 border-b ${sidebarTokens.border} last:border-0 ${className}`}>
        {title && (
            <h3 className={`px-4 mb-2 text-[10px] font-bold ${sidebarTokens.text.primary} uppercase tracking-wider`}>
                {title}
            </h3>
        )}
        <div className="px-4 space-y-2">
            {children}
        </div>
    </div>
);

interface SidebarItemProps {
    icon: React.ReactNode;
    label: string;
    isActive?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    shortcut?: string;
    badge?: string | number;
    color?: string; // Optional override for icon color
    collapsed?: boolean;
}

export const SidebarItem: React.FC<SidebarItemProps> = ({
    icon,
    label,
    isActive,
    onClick,
    shortcut,
    badge,
    color,
    collapsed
}) => {
    return (
        <button
            onClick={onClick}
            title={collapsed ? label : undefined}
            className={`
                group relative flex items-center w-full rounded-lg transition-all duration-200
                ${collapsed ? 'flex-col justify-center p-1 gap-1' : 'flex-row gap-3 px-3 py-2'}
                ${isActive
                    ? sidebarTokens.active + ' ' + sidebarTokens.text.active
                    : `text-text-muted ${sidebarTokens.hover} hover:text-text-main`
                }
            `}
        >
            <span className={`flex items-center justify-center ${isActive ? 'text-ui-accent' : (color || 'text-text-muted group-hover:text-text-main')}`}>
                {/* Clone element to force size if it's a valid react element, otherwise just render */}
                {React.isValidElement(icon)
                    ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
                        className: `w-6 h-6 ${((icon.props as { className?: string }).className || '')}`.trim()
                    })
                    : icon
                }
            </span>

            {/* Label for collapsed state */}
            {collapsed && (
                <span className="text-[9px] font-medium leading-3 text-center w-full px-0.5 whitespace-nowrap overflow-hidden text-ellipsis opacity-90 group-hover:opacity-100 tracking-tight">
                    {label.replace('\n', ' ')}
                </span>
            )}

            {!collapsed && (
                <>
                    <span className="flex-1 text-sm font-medium text-left truncate">
                        {label}
                    </span>
                    {shortcut && (
                        <span className="text-[10px] text-text-muted font-medium font-mono px-1.5 py-0.5 rounded bg-ui-surface/50 border border-ui-border">
                            {shortcut}
                        </span>
                    )}
                    {badge && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300">
                            {badge}
                        </span>
                    )}
                </>
            )}

            {/* Tooltip for collapsed state - disabled as we now show labels */}
            {/* {collapsed && (
                <div className="absolute left-full ml-3 px-2 py-1 bg-ui-surface text-text-main text-xs font-medium rounded opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all pointer-events-none whitespace-nowrap z-50 shadow-xl border border-ui-border">
                    {label}
                </div>
            )} */}

            {/* Active Indicator Bar (Left side) */}
            {isActive && collapsed && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-ui-accent rounded-r-full" />
            )}
        </button>
    );
};

export const SidebarDivider: React.FC<{ className?: string }> = ({ className = "" }) => (
    <div className={`h-px ${sidebarTokens.border} my-2 ${className}`} />
);

// --- Form Elements for Sidebar ---

export const SidebarLabel: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className = "" }) => (
    <label className={`block text-[10px] font-bold ${sidebarTokens.text.muted} uppercase tracking-wider mb-1.5 ${className}`}>
        {children}
    </label>
);

type SidebarButtonBaseProps = {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'icon';
    icon?: React.ComponentType<{ className?: string }>;
    children?: React.ReactNode;
    className?: string;
    as?: React.ElementType;
};

// Polymorphic types roughly
type SidebarButtonProps<E extends React.ElementType> = SidebarButtonBaseProps & Omit<React.ComponentProps<E>, keyof SidebarButtonBaseProps>;

export const SidebarButton = <E extends React.ElementType = 'button'>({
    children,
    variant = 'secondary',
    size = 'md',
    icon: Icon,
    className = "",
    as,
    ...props
}: SidebarButtonProps<E>) => {
    const Component = as || 'button';

    const variants = {
        primary: "bg-indigo-600 hover:bg-indigo-500 text-white shadow-sm shadow-indigo-500/20",
        secondary: "bg-ui-surface/50 hover:bg-ui-surface border border-ui-border text-text-muted hover:text-text-main",
        ghost: "hover:bg-ui-surface/50 text-text-muted hover:text-text-main",
        danger: "bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20"
    };

    const sizes = {
        sm: "px-2 py-1 text-xs",
        md: "px-3 py-1.5 text-xs",
        icon: "p-1.5"
    };

    return (
        <Component
            className={`
                flex items-center justify-center gap-2 rounded-md font-medium transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer
                ${variants[variant]} ${sizes[size]}
                ${className}
            `}
            {...props}
        >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {children}
        </Component>
    );
};

// --- New Inputs for Density Redesign ---

interface SidebarInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
}

export const SidebarInput = React.forwardRef<HTMLInputElement, SidebarInputProps>(
    ({ className = "", prefix, suffix, ...props }, ref) => (
        <div className={`group relative flex items-center bg-ui-surface/5 border border-ui-border hover:border-ui-border/50 focus-within:border-ui-accent/50 rounded-md transition-all h-6 w-full overflow-hidden ${className}`}>
            {prefix && (
                <div className="pl-2 pr-1 text-[10px] text-text-muted select-none font-bold flex items-center">
                    {prefix}
                </div>
            )}
            <input
                ref={ref}
                className="w-full bg-transparent border-none text-[10px] text-text-main placeholder-text-muted/50 px-2 focus:ring-0 focus:outline-none h-full"
                {...props}
            />
            {suffix && (
                <div className="pr-2 pl-1 text-[10px] text-text-muted select-none flex items-center">
                    {suffix}
                </div>
            )}
        </div>
    )
);

export const SidebarSegmentedControl: React.FC<{
    options: { value: string; label?: string; icon?: React.ReactNode }[];
    value: string;
    onChange: (value: string) => void;
    className?: string;
}> = ({ options, value, onChange, className = "" }) => (
    <div className={`flex bg-ui-main/50 p-0.5 rounded-lg border border-ui-border ${className}`}>
        {options.map((option) => {
            const isSelected = value === option.value;
            return (
                <button
                    key={option.value}
                    onClick={() => onChange(option.value)}
                    className={`
                        flex-1 flex items-center justify-center gap-1.5 py-1 px-2 rounded-md text-[10px] font-bold uppercase transition-all
                        ${isSelected
                            ? 'bg-ui-surface text-text-main shadow-sm'
                            : 'text-text-muted hover:text-text-main hover:bg-ui-surface/50'
                        }
                    `}
                    title={option.label}
                >
                    {option.icon && <span className={isSelected ? 'text-ui-accent' : 'text-current'}>{option.icon}</span>}
                    {option.label}
                </button>
            );
        })}
    </div>
);

export const SidebarGrid: React.FC<{ children: React.ReactNode, cols?: number, className?: string }> = ({
    children,
    cols = 2,
    className = ""
}) => (
    <div className={`grid grid-cols-${cols} gap-2 ${className}`}>
        {children}
    </div>
);

export const SidebarPropertyRow: React.FC<{
    label?: string;
    children: React.ReactNode;
    action?: React.ReactNode;
    className?: string;
}> = ({ label, children, action, className = "" }) => (
    <div className={`flex items-center justify-between gap-2 mb-2 ${className}`}>
        {label && <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{label}</span>}
        <div className="flex items-center gap-2 flex-1 min-w-0">
            {children}
        </div>
        {action}
    </div>
);
